import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppText from "@/components/Core/AppText/AppText";
import Button from "@/components/Core/Button/Button";
import {
	useFortnoxIntegrationStatus,
	useFortnoxTextIntegration,
	useOrganisation,
} from "@/networking/organisation.hooks";
import { useRefreshFortnoxCustomerDeliveryState } from "@/networking/invoice.hooks";
import { toast } from "@/utils/toast";
import { formatTimestamp } from "@/utils/date.time.utils";
import { SettingsLeaf } from "./SettingsLeafLayout";
import { IntegrationStatusRow } from "./integrationShared";
import { apiStore } from "../../../stores/api.store";
import { useAuthStore } from "../../../stores/auth.store";

/**
 * Shared Fortnox activate/test panel, reused by the "Fortnox fakturering" and
 * "Fortnox lön" leaves which differ only by integration `type`.
 */
const FortnoxIntegrationSection: React.FC<{ type: "invoice" | "salary"; title: string }> = ({
	type,
	title,
}) => {
	const authStore = useAuthStore();
	const { data, isLoading } = useFortnoxIntegrationStatus();
	const { mutateAsync: testIntegration, isLoading: isTesting } = useFortnoxTextIntegration();

	const status = type === "invoice" ? data?.invoice : data?.salary;

	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutateAsync: refreshDeliveryState, isLoading: isRefreshing } =
		useRefreshFortnoxCustomerDeliveryState();
	const [lastRefreshAt, setLastRefreshAt] = useState<string>();

	// Delivery state is only mirrored from Fortnox when Fortnox is the invoice export
	const canRefreshDeliveryState =
		type === "invoice" &&
		organisation?.invoiceSettings?.exportType === "FORTNOX" &&
		!!status?.activated;

	async function refresh() {
		const res = await refreshDeliveryState();
		setLastRefreshAt(res.observedAt);
		toast.success(
			t("deliveryMethod.refresh.success", {
				time: formatTimestamp(res.observedAt),
				managed: res.managed,
				updated: res.updated,
			})
		);
	}

	function activate() {
		if (
			window.confirm(
				"Du kommer nu bli vidarebefordrad till Fortnox där du loggar in och godkänner att Origo får nödvändig åtkomst."
			)
		) {
			// Open a new window that first creates a cookie login from the current
			// token (needed to keep the session through the Fortnox redirects), then
			// redirects into the Fortnox activation flow.
			const tokenToCookieUrl = `${apiStore.apiRoot || ""}/auth/token-to-cookie`;
			const activationUrl = `${apiStore.apiRoot || ""}/fortnox/activate?type=${type}`;
			window.open(
				tokenToCookieUrl +
					`?redirect=${encodeURIComponent(activationUrl)}&token=${encodeURIComponent(
						authStore.accessToken || ""
					)}`
			);
		}
	}

	async function test() {
		const res = await testIntegration({ type });
		window.alert(
			res.success ? "Test lyckades - koppling är aktiv och kan användas" : "Koppling misslyckades"
		);
	}

	if (isLoading) {
		return (
			<SettingsLeaf title={title}>
				<AppText>Loading...</AppText>
			</SettingsLeaf>
		);
	}

	return (
		<SettingsLeaf
			title={title}
			description="Skapa en API-anslutning till Fortnox för att skapa fakturaunderlag utifrån attesterade tidrapporter samt kundregister i Origo."
		>
			<div className="space-y-2">
				<IntegrationStatusRow
					activeTitle="Ja, tillgänglig för organisation"
					inactiveTitle="Ej tillgänglig, kontakta Origo för att aktivera"
					status={!!status?.enabledForOrganisation}
				/>
				<IntegrationStatusRow
					activeTitle="Ja, koppling är aktiverad"
					inactiveTitle="Koppling ej aktiverad"
					status={!!status?.activated}
				/>
			</div>

			<div className="flex gap-3">
				<Button disabled={!status?.enabledForOrganisation} onClick={activate}>
					{status?.activated ? "Återaktivera" : "Aktivera"} {title.toLowerCase()}
				</Button>
				<Button
					backgroundColor="black"
					loading={isTesting}
					disabled={!status?.enabledForOrganisation}
					onClick={test}
				>
					Testa koppling
				</Button>
			</div>

			{canRefreshDeliveryState && (
				<div className="space-y-2 border-t border-solid border-gray-200 pt-4">
					<p className="text-sm text-gray-600">{t("deliveryMethod.refresh.description")}</p>
					<Button loading={isRefreshing} onClick={() => refresh().catch(() => {})}>
						{t("deliveryMethod.refresh.button")}
					</Button>
					{lastRefreshAt && (
						<p className="text-xs text-gray-500">
							{t("deliveryMethod.refresh.lastRun", { time: formatTimestamp(lastRefreshAt) })}
						</p>
					)}
				</div>
			)}

			{status?.created && (
				<p className="text-xs text-gray-500">
					Koppling {status.activated ? "skapades" : "påbörjades"} {formatTimestamp(status.created)},
					senast uppdaterad {status.updated ? formatTimestamp(status.updated) : "n/a"}
				</p>
			)}
		</SettingsLeaf>
	);
};

export default FortnoxIntegrationSection;
